// @ts-nocheck
import React, { useState } from 'react';
import { animated, useSpring } from 'react-spring';
import { useDrag } from '@use-gesture/react';

interface User {
  id: number;
  name: string;
  age: number;
  photo: string;
  description: string;
  bio?: string;
  interests?: string[];
  location?: string;
  occupation?: string;
}

interface TinderCardProps {
  user: User;
  onSwipe: (direction: 'left' | 'right') => void;
  style?: any;
}

export const TinderCard: React.FC<TinderCardProps> = ({
  user,
  onSwipe,
  style,
}) => {
  const [showDetails, setShowDetails] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  const [{ x, y, rotate }, api] = useSpring(() => ({
    x: 0,
    y: 0,
    rotate: 0,
  }));

  // Используем простое состояние и CSS transitions как запасной вариант
  const [isDetailsVisible, setIsDetailsVisible] = useState(false);

  const bind = useDrag(
    ({ down, movement: [mx, my], direction: [dx, dy], velocity: [vx, vy] }) => {
      // Изменяем условие срабатывания на 30% от ширины окна
      const swipeThreshold = window.innerWidth * 0.2;
      const trigger = vx > 0.2 || Math.abs(mx) > swipeThreshold;
      const verticalTrigger = Math.abs(my) > 60;
      const dir = mx < 0 ? -1 : 1;

      // Проверяем свайп вверх для мгновенного открытия деталей
      if (
        down &&
        verticalTrigger &&
        dy < 0 &&
        !showDetails &&
        Math.abs(my) > Math.abs(mx)
      ) {
        toggleDetails();
        return;
      }

      // Обычная логика горизонтального свайпа (только если не свайпаем вертикально)
      if (!down && trigger && Math.abs(mx) > Math.abs(my)) {
        const direction = dir === 1 ? 'right' : 'left';
        // Карточка уезжает ровно по горизонтали за границу экрана
        api.start({
          x: (200 + window.innerWidth) * dir,
          y: 0, // Строго горизонтальное движение
          rotate: mx / 100 + (dir === 1 ? 1 : -1) * 10,
          config: { friction: 50, tension: 800 },
        });
        setTimeout(() => onSwipe(direction), 150);
      } else {
        // Обновляем позицию только по горизонтали, игнорируем вертикальное движение
        const shouldMoveHorizontally =
          Math.abs(mx) > Math.abs(my) || Math.abs(my) < 30;

        api.start({
          x: down && shouldMoveHorizontally ? mx : 0,
          y: 0, // Всегда 0 - не реагируем на вертикальные движения
          rotate: down && shouldMoveHorizontally ? mx / 100 : 0,
          immediate: down,
          config: { friction: 50, tension: down ? 800 : 500 },
        });
      }
    },
  );

  // Обработчик свайпа для полосочки закрытия
  const bindCloseHandle = useDrag(
    ({ down, movement: [mx, my], direction: [dx, dy], velocity: [vx, vy] }) => {
      const verticalTrigger = vy > 0.3 || Math.abs(my) > 50;

      // Проверяем свайп вниз для закрытия деталей
      if (!down && verticalTrigger && dy > 0 && showDetails) {
        toggleDetails();
      }
    },
  );

  const handleButtonClick = (direction: 'left' | 'right') => {
    const dir = direction === 'right' ? 1 : -1;
    // Карточка уезжает строго горизонтально за границу экрана
    api.start({
      x: (200 + window.innerWidth) * dir,
      // x: 1000,
      y: 0, // Строго горизонтальное движение
      rotate: dir * 10,
      config: { friction: 50, tension: 800 },
    });
    setTimeout(() => onSwipe(direction), 150);
  };

  const toggleDetails = () => {
    const newShowDetails = !showDetails;

    if (newShowDetails) {
      // Открываем панель
      setShowDetails(true);
      setIsDetailsVisible(true);
      // Небольшая задержка для запуска анимации после рендера
      setTimeout(() => {
        setIsAnimating(true);
      }, 10);
    } else {
      // Закрываем панель
      setIsAnimating(false);
      // Ждем завершения анимации перед размонтированием
      setTimeout(() => {
        setShowDetails(false);
        setIsDetailsVisible(false);
      }, 300); // Время должно совпадать с CSS transition
    }
  };

  return (
    <>
      <animated.div
        {...bind()}
        style={{
          x,
          y,
          rotate,
          ...style,
          touchAction: 'none',
        }}
        className='absolute h-full w-full cursor-grab select-none active:cursor-grabbing'
      >
        <div className='relative h-full w-full overflow-hidden rounded-xl bg-white shadow-2xl'>
          {/* Фоновое изображение */}
          <div
            className='absolute inset-0 bg-cover bg-center'
            style={{ backgroundImage: `url(${user.photo})` }}
          />

          {/* Градиент для читаемости текста */}
          <div className='absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent' />

          {/* Индикатор свайпа вверх */}
          {!showDetails && (
            <div className='absolute left-1/2 top-4 -translate-x-1/2 transform text-sm font-medium text-white/60'>
              ↑ Свайп вверх для подробностей
            </div>
          )}

          {/* Индикаторы свайпа */}
          <animated.div
            style={{
              opacity: x.to((x) => Math.max(0, x / 100)),
            }}
            className='absolute left-8 top-20 rotate-12 transform rounded-lg bg-green-500 px-4 py-2 text-lg font-bold text-white'
          >
            ЛАЙК
          </animated.div>

          <animated.div
            style={{
              opacity: x.to((x) => Math.max(0, -x / 100)),
            }}
            className='absolute right-8 top-20 -rotate-12 transform rounded-lg bg-red-500 px-4 py-2 text-lg font-bold text-white'
          >
            НЕЕТ
          </animated.div>

          {/* Контент карточки */}
          <div className='absolute bottom-0 left-0 right-0 p-6 text-white'>
            <h2 className='mb-2 cursor-pointer border-b border-transparent text-2xl font-bold transition-colors duration-200 hover:border-pink-300 hover:text-pink-300'>
              {user.name}, {user.age}
            </h2>
            <p className='mb-16 text-base leading-relaxed opacity-90'>
              {user.description}
            </p>

            {/* Кнопки управления */}
            <div className='absolute bottom-6 left-1/2 flex w-full -translate-x-1/2 transform justify-between space-x-4'>
              {/* Кнопка отклонения */}
              <button
                onClick={() => handleButtonClick('left')}
                className='flex h-12 w-64 items-center justify-center rounded-full bg-red-800 text-2xl text-white shadow-lg'
              >
                ✕
              </button>

              {/* Кнопка лайка */}
              <button
                onClick={toggleDetails}
                className='flex h-12 w-32 items-center justify-center rounded-full border-2 border-white bg-transparent text-2xl text-white shadow-lg transition-all duration-200 hover:scale-110 hover:shadow-xl active:scale-95'
              >
                i
              </button>

              {/* Кнопка лайка */}
              <button
                onClick={() => handleButtonClick('right')}
                className='flex h-12 w-64 items-center justify-center rounded-full bg-yellow-500 text-2xl text-white shadow-lg transition-all duration-200'
              >
                ♥
              </button>
            </div>
          </div>
        </div>
      </animated.div>

      {/* Дополнительная информационная панель */}
      {showDetails && (
        <div
          className={`details-panel fixed bottom-0 left-0 right-0 z-[9999] max-h-[70vh] overflow-y-auto rounded-t-3xl border-t-4 border-pink-200 bg-white shadow-2xl transition-all duration-300 ease-out ${
            isAnimating
              ? 'translate-y-0 opacity-100'
              : 'translate-y-full opacity-0'
          }`}
        >
          <div className='p-6'>
            {/* Индикатор для закрытия с возможностью свайпа */}
            <div className='relative mx-auto mb-2 flex w-full justify-center'>
              {/* Большая невидимая область для свайпа */}
              <div
                {...bindCloseHandle()}
                className='absolute -top-4 h-16 w-full cursor-pointer touch-none'
              />
              {/* Видимая полосочка */}
              <div className='h-1 w-12 rounded-full bg-gray-400 transition-colors hover:bg-gray-600' />
            </div>
            <div className='mb-4 text-center text-xs text-gray-500'>
              ↓ Свайп вниз для закрытия
            </div>

            {/* Заголовок */}
            <div className='mb-6 flex items-center justify-between'>
              <h3 className='text-2xl font-bold text-gray-800'>
                {user.name}, {user.age}
              </h3>
              <button
                onClick={toggleDetails}
                className='flex h-8 w-8 items-center justify-center rounded-full bg-gray-200 text-gray-600 transition-colors hover:bg-gray-300'
              >
                ✕
              </button>
            </div>

            {/* Дополнительная информация */}
            <div className='space-y-4'>
              {user.location && (
                <div>
                  <h4 className='mb-1 text-sm font-semibold uppercase tracking-wide text-gray-500'>
                    Местоположение
                  </h4>
                  <p className='text-gray-800'>📍 {user.location}</p>
                </div>
              )}

              {user.occupation && (
                <div>
                  <h4 className='mb-1 text-sm font-semibold uppercase tracking-wide text-gray-500'>
                    Профессия
                  </h4>
                  <p className='text-gray-800'>💼 {user.occupation}</p>
                </div>
              )}

              {user.bio && (
                <div>
                  <h4 className='mb-1 text-sm font-semibold uppercase tracking-wide text-gray-500'>
                    О себе
                  </h4>
                  <p className='leading-relaxed text-gray-800'>{user.bio}</p>
                </div>
              )}

              {user.interests && user.interests.length > 0 && (
                <div>
                  <h4 className='mb-2 text-sm font-semibold uppercase tracking-wide text-gray-500'>
                    Интересы
                  </h4>
                  <div className='flex flex-wrap gap-2'>
                    {user.interests.map((interest, index) => (
                      <span
                        key={index}
                        className='rounded-full bg-pink-100 px-3 py-1 text-sm font-medium text-pink-600'
                      >
                        {interest}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              <div>
                <h4 className='mb-1 text-sm font-semibold uppercase tracking-wide text-gray-500'>
                  Описание
                </h4>
                <p className='leading-relaxed text-gray-800'>
                  {user.description}
                </p>
              </div>
            </div>

            {/* Кнопки действий в панели */}
            <div className='mt-8 flex gap-4 border-t border-gray-200 pt-6'>
              <button
                onClick={() => {
                  toggleDetails();
                  handleButtonClick('left');
                }}
                className='flex h-12 w-64 items-center justify-center rounded-full bg-red-800 text-2xl text-white shadow-lg'
              >
                ✕
              </button>
              <button
                onClick={() => {
                  toggleDetails();
                  handleButtonClick('right');
                }}
                className='flex h-12 w-64 items-center justify-center rounded-full bg-yellow-500 text-2xl text-white shadow-lg transition-all duration-200'
              >
                ♥
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Оверлей для закрытия панели */}
      {showDetails && (
        <div
          className='fixed inset-0 z-[9998] bg-black/30'
          onClick={toggleDetails}
        />
      )}
    </>
  );
};
